import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import { useSelector, useDispatch, useStore, shallowEqual } from 'react-redux'
import useAuth from 'src/hooks/useAuth'
import useApi from 'src/hooks/useApi'
import {getDashConfig} from "../utils/localStore";
import { DynamoDB } from 'aws-sdk';
import { drainMessages } from "../redux/slices/stream";
import {updateAssetsFromStream} from "../redux/slices/api";
import useInterval from 'react-use/lib/useInterval'



const AssetsContext = React.createContext({
  fetching: true,
  assets: undefined,
  editMode:false,
  gridCols: 4,
  reorderMode: false,
  setReorderMode: () => {}
})

const STREAM_DRAIN_INTERVAL_MS = 1000;
const PULSE_REFETCH_MIN_INTERVAL_MS = 10000;
const SITE_FETCH_MIN_INTERVAL_MS = 5000;

const PULSE_FIELDS = ['pulseActive', 'nextPulseTime', 'lastPulseTime'];

function getReadingTime(reading) {
  if (!reading) return null;
  const timestamp = Date.parse(reading);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function isNotNewerStreamReading(incoming, current) {
  const incomingTime = getReadingTime(incoming?.lastReading);
  const currentTime = getReadingTime(current?.lastReading);

  return incomingTime !== null && currentTime !== null && incomingTime <= currentTime;
}

function isNotNewerThanAnyCurrentReading(incoming, currentReadings) {
  return currentReadings.some(current => isNotNewerStreamReading(incoming, current));
}

function getStreamDebugConfig() {
  if (typeof window === 'undefined') {
    return { enabled: false };
  }

  try {
    return {
      enabled: window.localStorage.getItem('frigidStreamDebug') === '1',
      assetId: window.localStorage.getItem('frigidStreamDebugAsset') || null,
    };
  } catch {
    return { enabled: false };
  }
}

function shouldDebugStreamMessage(msg) {
  const { enabled, assetId } = getStreamDebugConfig();
  if (!enabled) return false;
  if (!assetId) return true;

  return msg.assetId === assetId || msg.id === assetId || msg.id?.startsWith(`${assetId}_`);
}

function logStreamReadingDecision(decision, msg, currentReadings) {
  if (!shouldDebugStreamMessage(msg)) return;

  const current = currentReadings.map(reading => ({
    id: reading.id,
    SK: reading.SK,
    value: reading.value,
    lastReading: reading.lastReading,
  }));

  console.info(`[stream ${decision}]`, {
    assetId: msg.assetId,
    id: msg.id,
    SK: msg.SK,
    value: msg.value,
    lastReading: msg.lastReading,
    receivedAt: new Date().toISOString(),
    current,
  });
}

// Debug-gated log - enable with localStorage.frigidStreamDebug = '1'
function logStreamDebug(msg, ...args) {
  if (!shouldDebugStreamMessage(msg)) return;
  console.log(...args);
}

// Did the incoming record change any pulse-related field vs the current record?
function pulseFieldsChanged(msg, current) {
  return PULSE_FIELDS.some(f => msg[f] !== undefined && msg[f] !== current?.[f]);
}

export function AssetsProvider({ children }) {
  const auth = useAuth()
  const api = useApi()
  const dispatch = useDispatch()
  const store = useStore()
  let site = api.site

  // api identity changes every render - keep a ref so callbacks/intervals can
  // stay referentially stable (otherwise every consumer re-renders constantly).
  const apiRef = useRef(api);
  apiRef.current = api;

  const [fetching, setFetching] = useState(true)
  const [loadingAssets, setLoadingAssets] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [gridCols, setGridCols] = useState(getDashConfig()?.gridCols ?? 4)
  const [searchterm, setSearchTerm] = useState('')
  const [tagFilter, setTagFilter] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);

  // shallowEqual: only re-render when the filtered membership actually changes
  // (the filter builds a fresh array on every store dispatch otherwise).
  let assets = useSelector((state) => {
    let filtered = state.api.assets?.filter(asset => {
      return asset?.reference?.toLowerCase().includes(searchterm.toLowerCase()) ||
          asset?.name?.toLowerCase().includes(searchterm.toLowerCase()) ||
          asset?.brew?.batchNumber.toLowerCase().includes(searchterm.toLowerCase()) ||
          asset?.id?.toLowerCase().includes(searchterm.toLowerCase());
    });
    if (tagFilter.length > 0) {
        filtered = filtered.filter(asset => {
            return (
                asset?.brew?.tags?.some(tag => tagFilter.includes(tag)) ||
                asset?.tags?.some(tag => tagFilter.includes(tag))
            );
            });
    }
    let out = state.api.assets.length > 0 ? filtered : null;

    return out
  }, shallowEqual);

  /**
   * Automatically resolve the user session on direct load of any route
   */
  useEffect(() => {
    async function initAuth() {
      if (auth && !auth.user) {
        try {
          await auth.checkUser();
        } catch (e) {
          console.error("Failed to restore authenticated user session on mount:", e);
        }
      }
    }
    initAuth();
  }, [auth]);

  /**
   * When we have an authenticated user, fetch a list of all the assets
   */
  useEffect(() => {
    if (auth.user) {
      setLoadingAssets(true)
      apiRef.current.listAssets(() => {
        setLoadingAssets(false)
        apiRef.current.listTemplates && apiRef.current.listTemplates()
      })
    }
  }, [auth.user])

  /**
   * Load the site data payload once at startup so the merged site fields
   * (hfMode, beta, config, etc.) are present. Ongoing site freshness is
   * handled by the WebSocket SITE# handler and explicit post-mutation refetches.
   */
  useEffect(() => {
    if (auth.user) api.getSiteData()
  }, [auth.user])

  /**
   * Call to get fresh asset data
   */
  const refetch = useCallback(async () => {
    setFetching(true)
    try {
      if (auth.user) {
        await apiRef.current.getSiteData()
        await apiRef.current.listAssets()
      }
    } finally {
      setFetching(false)
    }
  }, [auth.user])

  /**
   * Assets-only refresh for the dashboard poll
   */
  const refetchAssets = useCallback(async () => {
    setFetching(true)
    try {
      if (auth.user) {
        await apiRef.current.listAssets()
      }
    } finally {
      setFetching(false)
    }
  }, [auth.user])

  useInterval(() => {
    if (reorderMode) return;
    refetchAssets && refetchAssets();
  }, site?.hfMode ? 3000 : 15000);

  /**
   * Drain buffered websocket stream messages on a fixed cadence rather than
   * per message. Bursts (pulse loops, whole-site asset sweeps) are processed
   * in one pass with a single store update, so the grid re-renders at most
   * once per interval instead of per message. (react-use keeps the latest
   * callback, so this closure always sees current state.)
   */
  const lastPulseRefetchRef = useRef(0);
  const lastSiteFetchRef = useRef(0);

  useInterval(() => {
    const state = store.getState();
    const messages = state.stream.messages;
    if (!messages || messages.length === 0) return;
    if (state.stream.pauseCount > 0) return; // paused (e.g. asset dialog open)

    const processedCount = messages.length;
    const allAssets = state.api.assets || [];
    let updatedAssets = Object.assign([], allAssets); // Create a copy of the current assets array
    let changed = false;
    let pulseChanged = false;
    let siteUpdated = false;

    messages.forEach(streamMsg => {
      // DynamoDB stream events: REMOVEs carry OldImage only, INSERT/MODIFY carry NewImage
      const isRemove = streamMsg.event === 'REMOVE' || !streamMsg.new;
      const msg = DynamoDB.Converter.unmarshall(isRemove ? streamMsg.old : streamMsg.new);
      if (!msg || !msg.SK) return;

      if (isRemove) {
        // a row was deleted (e.g. asset deleted via API) - drop it from the local list
        if (msg.SK.startsWith('ASSET#') && !msg.SK.includes('#INPUT') && !msg.SK.includes('#OUTPUT')) {
          const idx = updatedAssets.findIndex(a => a.SK === msg.SK);
          if (idx !== -1) {
            updatedAssets.splice(idx, 1);
            changed = true;
            logStreamDebug(msg, 'Removed asset from stream', msg.id);
          }
        }
        return;
      }

      if (msg.SK.startsWith('ASSET#') && !msg.SK.includes('#INPUT') && !msg.SK.includes('#OUTPUT')) {
        //it could be an asset, input or output
        const assetIndex = updatedAssets.findIndex(a => a.SK === msg.SK);
        if (assetIndex !== -1) {
          const asset = updatedAssets[assetIndex];
          if (asset == null) return;
          if (pulseFieldsChanged(msg, asset)) pulseChanged = true;
          updatedAssets[assetIndex] = {
            ...msg,
            brew: asset.brew,
            brewLogs: asset.brewLogs,
            input: asset.input,
            inputs: asset.inputs,
            output: asset.output,
            outputs: asset.outputs
          };
          changed = true;
          logStreamDebug(msg, 'Updated asset from stream', msg.id);
        } else if (PULSE_FIELDS.some(f => msg[f] !== undefined)) {
          // unknown asset carrying pulse state - preserve old refetch behavior
          pulseChanged = true;
        }

      } else if (msg.SK.includes('#INPUT') || msg.SK.includes('#OUTPUT')) {
        const assetIndex = updatedAssets.findIndex(a => a.id === msg.assetId);
        const asset = updatedAssets[assetIndex];
        if (asset) {

          if (msg.SK.includes('#INPUT')) {
            // Use the log-input timestamp from DynamoDB when available, fall back to browser time
            const updatedMsg = { ...msg, lastReading: msg.lastReading || new Date().toISOString() };
            const currentInput = asset.inputs?.find(input => input.SK === msg.SK);
            const currentReadings = [
              currentInput,
              asset.input?.SK === msg.SK ? asset.input : null
            ].filter(Boolean);

            if (isNotNewerThanAnyCurrentReading(updatedMsg, currentReadings)) {
              logStreamReadingDecision('input stale', updatedMsg, currentReadings);
              console.warn('Ignored stale input stream update', msg.id, {
                incomingLastReading: updatedMsg.lastReading,
                currentLastReading: currentReadings.map(reading => reading.lastReading)
              });
              return;
            }

            logStreamReadingDecision('input accepted', updatedMsg, currentReadings);
            if (pulseFieldsChanged(updatedMsg, currentInput)) pulseChanged = true;

            updatedAssets[assetIndex] = {
              ...asset,
              input: asset.input?.SK === msg.SK ? updatedMsg : asset.input,
              inputs: asset.inputs.map(input =>
                  input.SK === msg.SK ? updatedMsg : input
              ),
            };
            changed = true;
            logStreamDebug(msg, 'Updated input from stream', msg.id);
          } else if (msg.SK.includes('#OUTPUT')) {
            const currentOutput = asset.outputs?.find(output => output.SK === msg.SK);
            const currentReadings = [
              currentOutput,
              asset.output?.SK === msg.SK ? asset.output : null
            ].filter(Boolean);

            if (isNotNewerThanAnyCurrentReading(msg, currentReadings)) {
              logStreamReadingDecision('output stale', msg, currentReadings);
              console.warn('Ignored stale output stream update', msg.id, {
                incomingLastReading: msg.lastReading,
                currentLastReading: currentReadings.map(reading => reading.lastReading)
              });
              return;
            }

            logStreamReadingDecision('output accepted', msg, currentReadings);
            if (pulseFieldsChanged(msg, currentOutput)) pulseChanged = true;

            updatedAssets[assetIndex] = {
              ...asset,
              output: asset.output?.SK === msg.SK ? msg : asset.output,
              outputs: asset.outputs.map(output =>
                  output.SK === msg.SK ? msg : output
              ),
            };
            changed = true;
            logStreamDebug(msg, 'Updated output from stream', msg.id);
          }
        }

      } else if (msg.SK.startsWith('BREW#')) {
        let brewApplied = false;
        updatedAssets.forEach((asset, index) => {
          if (asset && asset.brew && asset.brew.id === msg.id) {
            logStreamDebug(msg, 'Matching brew found for asset', asset.id);

            updatedAssets[index] = {
              ...asset,
              brew: {...asset.brew, ...msg},
            };
            brewApplied = true;
          }
        });
        if (brewApplied) changed = true;
        logStreamDebug(msg, 'Updated brew from stream', msg.id);

      } else if (msg.SK.startsWith('SITE#')) {
          //site data updated
          logStreamDebug(msg, 'Site data updated from stream', msg.id);
          //just fetch again.. (lazy) - throttled: one site fetch per few seconds max
          siteUpdated = true;
      } else {
        logStreamDebug(msg, 'no match', msg);
      }

    });

    // Single store update for the whole batch (skipped entirely if nothing applied)
    if (changed) {
      dispatch(updateAssetsFromStream(updatedAssets));
    }

    // Drop exactly the messages we processed (anything that arrived during
    // processing stays buffered for the next tick)
    dispatch(drainMessages(processedCount));

    const now = Date.now();

    // Pulse state (pulseActive/nextPulseTime) is merged locally above; this is a
    // throttled backstop so the PulseCountdownIndicator tracks the real
    // pulse/hold state without a network waterfall per message.
    if (pulseChanged && refetch && now - lastPulseRefetchRef.current > PULSE_REFETCH_MIN_INTERVAL_MS) {
      lastPulseRefetchRef.current = now;
      refetch();
    }

    if (siteUpdated && !reorderMode && window.location.pathname.startsWith('/app')
        && now - lastSiteFetchRef.current > SITE_FETCH_MIN_INTERVAL_MS) {
      lastSiteFetchRef.current = now;
      apiRef.current.getSiteData();
    }
  }, STREAM_DRAIN_INTERVAL_MS);

  // Resync when the websocket stream resumes after being paused (e.g. an asset
  // dialog was closed) - messages dropped while paused are recovered via a
  // full refetch.
  const streamPauseCount = useSelector(state => state.stream.pauseCount);
  const prevStreamPauseCountRef = useRef(0);
  useEffect(() => {
    const wasPaused = prevStreamPauseCountRef.current > 0;
    prevStreamPauseCountRef.current = streamPauseCount;
    if (wasPaused && streamPauseCount === 0) {
      refetch && refetch();
    }
  }, [streamPauseCount, refetch]);


  // Memoized so context consumers (every AssetCard) only re-render when actual
  // data changes - not on every provider render.
  const contextValue = useMemo(() => ({
    site, assets, fetching: fetching || loadingAssets, refetch, refetchAssets,
    editMode, setEditMode, gridCols, setGridCols,
    setSearchTerm, setTagFilter, reorderMode, setReorderMode
  }), [site, assets, fetching, loadingAssets, refetch, refetchAssets, editMode, gridCols, reorderMode]);

  return (
    <AssetsContext.Provider value={contextValue}>
      {children}
    </AssetsContext.Provider>
  )
}

export function useAssetsContext() {
  const context = useContext(AssetsContext)

  if (!context) {
    throw new Error('No AssetsProvider was found.')
  }

  return context
}
